import { supabase } from "@/integrations/supabase/client";
import type { OCRResult } from "@/components/CameraScanner";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import { onlyActiveCompany } from "@/lib/activeCompanyLookup";

type ApplianceMatch = {
  id: string;
  name: string | null;
  brand: string | null;
  model_number: string | null;
  serial_number: string | null;
  product_number: string | null;
  company_id?: string | null;
};

type WorkOrderMatch = {
  id: string;
  type: string;
  status: string | null;
  appliance_id: string;
  updated_at: string | null;
  created_at: string | null;
};

export type ScanDestination = {
  path: string;
  kind: "work-order" | "appliance";
  applianceId: string;
  workOrderId?: string;
};

const normalize = (value?: string | null) =>
  (value || "").toLowerCase().replace(/[\s\-\/_.]/g, "");

const escapeLike = (value: string) => value.replace(/[\\%_]/g, "\\$&");

const workflowKeyForType = (type?: string | null) => {
  if (type === "harvest") return "harvesting";
  if (type === "refurbish") return "refurbishment";
  return type || "harvesting";
};

const scoreAppliance = (row: ApplianceMatch, ocrResult: OCRResult) => {
  const model = normalize(ocrResult.modelNumber);
  const product = normalize(ocrResult.productNumber);
  const serial = normalize(ocrResult.serialNumber);
  const brand = normalize(ocrResult.brand);
  const rowModel = normalize(row.model_number);
  const rowProduct = normalize(row.product_number);
  const rowSerial = normalize(row.serial_number);
  const rowBrand = normalize(row.brand || row.name?.split(" ")[0]);

  let score = 0;
  if (serial && rowSerial && serial === rowSerial) score += 120;
  if (product && rowProduct && product === rowProduct) score += 90;
  if (model && rowModel) {
    if (rowModel === model) score += 80;
    else if (rowModel.includes(model) || model.includes(rowModel)) score += 60;
  }
  if (brand && rowBrand && (rowBrand.includes(brand) || brand.includes(rowBrand))) score += 10;
  return score;
};

export const collectApplianceMatches = async (ocrResult: OCRResult, companyId: string): Promise<ApplianceMatch[]> => {
  const searches = [ocrResult.modelNumber, ocrResult.productNumber, ocrResult.serialNumber]
    .map((value) => value?.trim())
    .filter((value): value is string => !!value);

  if (searches.length === 0) return [];

  const results = await Promise.all(
    searches.map((value) =>
      supabase
        .from("appliances")
        .select("id, name, brand, model_number, serial_number, product_number, company_id")
        .eq("company_id", companyId)
        .or(
          `model_number.ilike.%${escapeLike(value)}%,product_number.ilike.%${escapeLike(value)}%,serial_number.ilike.%${escapeLike(value)}%`,
        )
        .limit(20),
    ),
  );

  const byId = new Map<string, ApplianceMatch>();
  results.forEach(({ data, error }) => {
    if (error) {
      console.error("Failed to resolve scanned appliance:", error);
      return;
    }
    (data || []).forEach((row) => byId.set(row.id, row as ApplianceMatch));
  });

  return onlyActiveCompany([...byId.values()], companyId)
    .map((row) => ({ row, score: scoreAppliance(row, ocrResult) }))
    .filter(({ score }) => score >= 60)
    .sort((a, b) => b.score - a.score)
    .map(({ row }) => row);
};

const findBestWorkOrder = async (applianceId: string, companyId: string): Promise<WorkOrderMatch | null> => {
  const { data, error } = await supabase
    .from("work_orders")
    .select("id, type, status, appliance_id, updated_at, created_at")
    .is("deleted_at", null)
    .eq("company_id", companyId)
    .eq("appliance_id", applianceId)
    .order("updated_at", { ascending: false })
    .limit(20);

  if (error) {
    console.error("Failed to resolve scanned work order:", error);
    return null;
  }

  const orders = ((data || []) as WorkOrderMatch[]).sort((a, b) => {
    const statusScore = (status: string | null) =>
      status === "in_progress" || status === "pending" ? 2 : status === "completed" ? 1 : 0;
    const statusDelta = statusScore(b.status) - statusScore(a.status);
    if (statusDelta !== 0) return statusDelta;
    return new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime();
  });

  return orders[0] || null;
};

export const resolveScanDestination = async (ocrResult: OCRResult): Promise<ScanDestination | null> => {
  const companyId = await fetchCurrentCompanyId();
  if (!companyId) return null;
  const [appliance] = await collectApplianceMatches(ocrResult, companyId);
  if (!appliance) return null;

  const workOrder = await findBestWorkOrder(appliance.id, companyId);
  if (workOrder) {
    const workflowKey = workflowKeyForType(workOrder.type);
    return {
      kind: "work-order",
      applianceId: appliance.id,
      workOrderId: workOrder.id,
      path: `/appliance/${appliance.id}/workflow/${workflowKey}?wo=${workOrder.id}&workOrderId=${workOrder.id}`,
    };
  }

  return {
    kind: "appliance",
    applianceId: appliance.id,
    path: `/appliance/${appliance.id}`,
  };
};